import {
  Badge,
  Card,
  Container,
  Flex,
  HStack,
  Heading,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useState } from "react"
import { FiFilter, FiGrid, FiList, FiUpload } from "react-icons/fi"

import { Button } from "@/components/atoms/Button"
import { PermissionGate } from "@/features/auth"
import { usePermissions } from "@/hooks/usePermissions"
import FileList from "./FileList"
import FileUpload from "./FileUpload"

export interface FileManagerProps {
  uploadType?: string
  title?: string
  showUpload?: boolean
  showList?: boolean
  onFileSelect?: (file: unknown) => void
  selectable?: boolean
}

type ViewMode = "upload" | "list" | "both"

export const FileManager = ({
  uploadType = "attachment",
  title = "File Manager",
  showUpload = true,
  showList = true,
  onFileSelect,
  selectable = false,
}: FileManagerProps) => {
  const { canCreateFiles, canReadFiles } = usePermissions()

  // Determine what features to show based on permissions
  const effectiveShowUpload = showUpload && canCreateFiles
  const effectiveShowList = showList && canReadFiles

  const [viewMode, setViewMode] = useState<ViewMode>(
    effectiveShowUpload && effectiveShowList
      ? "both"
      : effectiveShowUpload
        ? "upload"
        : "list",
  )

  const getViewModeOptions = () => {
    const options = []
    if (effectiveShowUpload && effectiveShowList) {
      options.push(
        { value: "upload", label: "Upload", icon: FiUpload },
        { value: "list", label: "Files", icon: FiList },
        { value: "both", label: "Both", icon: FiGrid },
      )
    } else if (effectiveShowUpload) {
      options.push({ value: "upload", label: "Upload", icon: FiUpload })
    } else if (effectiveShowList) {
      options.push({ value: "list", label: "Files", icon: FiList })
    }
    return options
  }

  const viewModeOptions = getViewModeOptions()

  const handleUploadComplete = (fileInfo: unknown) => {
    // When a file is uploaded, switch to list view to show it
    if (effectiveShowList && viewMode === "upload") {
      setViewMode("list")
    }
    onFileSelect?.(fileInfo)
  }

  return (
    <Container maxW="full" p={0}>
      <Stack gap={6}>
        {/* Header */}
        <Flex align="center" justify="space-between">
          <Stack gap={1}>
            <Heading size="md">{title}</Heading>
            <HStack>
              <Badge variant="outline" size="sm">
                {uploadType}
              </Badge>
              {selectable && (
                <Badge colorScheme="blue" size="sm">
                  Selectable
                </Badge>
              )}
            </HStack>
          </Stack>

          {/* View Mode Toggle */}
          {viewModeOptions.length > 1 && (
            <HStack>
              {viewModeOptions.map((option) => {
                const Icon = option.icon
                return (
                  <Button
                    key={option.value}
                    size="sm"
                    variant={viewMode === option.value ? "solid" : "outline"}
                    onClick={() => setViewMode(option.value as ViewMode)}
                    gap={1}
                  >
                    <Icon />
                    {option.label}
                  </Button>
                )
              })}
            </HStack>
          )}
        </Flex>

        {/* Content */}
        <Stack gap={6}>
          {/* Upload Section */}
          {effectiveShowUpload &&
            (viewMode === "upload" || viewMode === "both") && (
              <PermissionGate
                scope="files:create"
                fallback={
                  <Card.Root>
                    <Card.Header>
                      <Heading size="sm">Upload Files</Heading>
                    </Card.Header>
                    <Card.Body>
                      <Text color="gray.500" textAlign="center" py={4}>
                        You need "files:create" permission to upload files.
                      </Text>
                    </Card.Body>
                  </Card.Root>
                }
              >
                <Card.Root>
                  <Card.Header>
                    <Heading size="sm">Upload Files</Heading>
                  </Card.Header>
                  <Card.Body>
                    <FileUpload
                      onUploadComplete={handleUploadComplete}
                      uploadType={uploadType}
                    />
                  </Card.Body>
                </Card.Root>
              </PermissionGate>
            )}

          {/* File List Section */}
          {effectiveShowList &&
            (viewMode === "list" || viewMode === "both") && (
              <PermissionGate
                scope="files:read"
                fallback={
                  <Card.Root>
                    <Card.Header>
                      <Flex align="center" justify="space-between">
                        <Heading size="sm">Your Files</Heading>
                        <Badge variant="outline" size="sm" gap={1}>
                          <FiFilter />
                          {uploadType}
                        </Badge>
                      </Flex>
                    </Card.Header>
                    <Card.Body>
                      <Text color="gray.500" textAlign="center" py={4}>
                        You need "files:read" permission to view files.
                      </Text>
                    </Card.Body>
                  </Card.Root>
                }
              >
                <Card.Root>
                  <Card.Header>
                    <Flex align="center" justify="space-between">
                      <Heading size="sm">Your Files</Heading>
                      <Badge variant="outline" size="sm" gap={1}>
                        <FiFilter />
                        {uploadType}
                      </Badge>
                    </Flex>
                  </Card.Header>
                  <Card.Body>
                    <FileList
                      uploadType={uploadType}
                      onFileSelect={onFileSelect}
                      selectable={selectable}
                    />
                  </Card.Body>
                </Card.Root>
              </PermissionGate>
            )}

          {/* No Permissions Message */}
          {!effectiveShowUpload && !effectiveShowList && (
            <Card.Root>
              <Card.Body>
                <Text color="gray.500" textAlign="center" py={8}>
                  You don't have sufficient permissions to access file
                  management features.
                  <br />
                  Required permissions: "files:read" or "files:create"
                </Text>
              </Card.Body>
            </Card.Root>
          )}
        </Stack>
      </Stack>
    </Container>
  )
}

export default FileManager
